import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { FlaskConical } from "lucide-react";
import { SPCChart } from "@/components/charts/SPCChart";
import { FilterBar } from "@/components/dashboard/FilterBar";
import { HelpIcon } from "@/components/HelpTooltip";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";
import { useQAParts, useSPCData } from "@/hooks/use-quality";
import { useWeightDrift, useInSpecTrend, useInspectorConsistency } from "@/hooks/use-analytics";
import { formatNumber, formatPct, formatDate, daysAgo, today } from "@/lib/utils";
import type { SPCAlert } from "@shared/types";

/** Group alerts by rule + description for the summary panel */
function groupAlerts(alerts: SPCAlert[]) {
  const map = new Map<string, { rule: string; severity: "warning" | "critical"; description: string; count: number }>();
  for (const a of alerts) {
    const key = `${a.rule}_${a.description}`;
    const existing = map.get(key);
    if (existing) {
      existing.count++;
    } else {
      map.set(key, { rule: a.rule, severity: a.severity, description: a.description, count: 1 });
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    if (a.severity === "critical" && b.severity !== "critical") return -1;
    if (b.severity === "critical" && a.severity !== "critical") return 1;
    return b.count - a.count;
  });
}

// Measurement types available for SPC charts — loaded dynamically per product.
// This static fallback is used before a product is selected.
const DEFAULT_MEASUREMENT = "weight_g";

export function QualityControl() {
  useSetPageHeader({
    title: "Control Charts",
    subtitle: "SPC control charts with statistical analysis",
    icon: FlaskConical,
  });

  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState(() => searchParams.get("product") || "");
  const [test, setTest] = useState(DEFAULT_MEASUREMENT);
  const [from, setFrom] = useState(() => daysAgo(365));
  const [to, setTo] = useState(() => today());
  const [operator, setOperator] = useState("");
  const [shift, setShift] = useState("");
  const products = useQAParts();
  const spc = useSPCData(product, test, from, to, {
    operator: operator || undefined,
    shift: shift || undefined,
  });
  const strengthModel = useWeightDrift(product);
  const [trendBucket, setTrendBucket] = useState<"week" | "month">("week");
  const inSpecTrend = useInSpecTrend(product, test, from, to, trendBucket);
  const analystData = useInspectorConsistency(product, test, from, to);

  // Auto-select first product and its primary measurement when loaded
  if (!product && products.data?.length) {
    setProduct(products.data[0].slug);
    setTest(products.data[0].primaryMeasurement || DEFAULT_MEASUREMENT);
  }

  // Get measurements for the selected product
  const selectedProduct = products.data?.find((p) => p.slug === product);
  const measurements = selectedProduct?.measurements || [];
  const testLabel = measurements.find((m) => m.key === test)?.label || test;

  return (
    <div className="space-y-6">
      {/* Product selector */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-muted-foreground">Product:</label>
        <select
          value={product}
          onChange={(e) => {
            const slug = e.target.value;
            setProduct(slug);
            const p = products.data?.find((x) => x.slug === slug);
            setTest(p?.primaryMeasurement || DEFAULT_MEASUREMENT);
          }}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground"
        >
          {products.data?.map((p) => (
            <option key={p.slug} value={p.slug}>
              {p.label} ({formatNumber(p.sample_count)} samples)
            </option>
          ))}
        </select>
      </div>

      {/* Measurement type tabs — dynamic per product */}
      {measurements.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="mr-2 text-xs font-medium text-muted-foreground">
            Measurement
          </span>
          {measurements.map((m) => (
            <button
              key={m.key}
              onClick={() => setTest(m.key)}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                test === m.key
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-accent"
              }`}
            >
              {m.label}{m.unit ? ` (${m.unit})` : ""}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <FilterBar from={from} to={to} onFromChange={setFrom} onToChange={setTo}>
        <input
          type="text"
          placeholder="Operator"
          value={operator}
          onChange={(e) => setOperator(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground w-32"
        />
        <input
          type="text"
          placeholder="Shift"
          value={shift}
          onChange={(e) => setShift(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground w-32"
        />
      </FilterBar>

      {/* SPC Chart */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="mb-2 text-lg font-semibold text-card-foreground">
          {testLabel}
        </h2>
        {spc.isLoading ? (
          <div className="flex h-80 items-center justify-center text-muted-foreground">
            Loading SPC data...
          </div>
        ) : spc.error ? (
          <div className="flex h-80 items-center justify-center text-destructive">
            Error loading data: {spc.error.message}
          </div>
        ) : spc.data?.unavailable ? (
          <div className="flex h-80 flex-col items-center justify-center gap-1">
            <p className="text-muted-foreground">
              This test type is not measured for the selected product
            </p>
            <p className="text-xs text-muted-foreground/60">
              The {test.replace(/_/g, " ")} column does not exist in this product's QA table
            </p>
          </div>
        ) : spc.data ? (
          <SPCChart
            data={spc.data.data}
            stats={spc.data.stats}
            spec={spc.data.spec}
            alerts={spc.data.alerts}
            testLabel={testLabel}
            unit={measurements.find((m) => m.key === test)?.unit || ""}
          />
        ) : null}
      </div>

      {/* Nelson Rule alerts */}
      {spc.data && spc.data.alerts.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="mb-3 text-lg font-semibold text-card-foreground">
            Process Alerts ({spc.data.alerts.length})
            <HelpIcon k="nelson_rules" />
          </h2>
          <div className="space-y-2">
            {groupAlerts(spc.data.alerts).map((group, i) => (
              <div
                key={i}
                className={`flex items-start gap-3 rounded-lg border p-3 ${
                  group.severity === "critical"
                    ? "border-red-500/30 bg-red-500/5"
                    : "border-purple-500/30 bg-purple-500/5"
                }`}
              >
                <span className={`mt-0.5 text-sm font-bold ${
                  group.severity === "critical" ? "text-red-400" : "text-purple-400"
                }`}>
                  {group.rule === "rule_1" ? "R1" : group.rule === "rule_2" ? "R2" : group.rule === "rule_3" ? "R3" : group.rule === "rule_5" ? "R5" : "R6"}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{group.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {group.count} point{group.count > 1 ? "s" : ""} flagged
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats panel */}
      {spc.data && !spc.data.unavailable && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <StatCard label="Mean" value={spc.data.stats.mean.toFixed(2)} />
          <StatCard label="Std Dev" value={spc.data.stats.stddev.toFixed(2)} helpKey="stddev" />
          <StatCard label="UCL" value={spc.data.stats.ucl.toFixed(2)} helpKey="ucl" />
          <StatCard label="LCL" value={spc.data.stats.lcl.toFixed(2)} helpKey="lcl" />
          <StatCard
            label="Samples"
            value={formatNumber(spc.data.stats.count)}
          />
          <StatCard
            label="In Spec"
            value={formatPct(spc.data.stats.in_spec_pct)}
            status={
              spc.data.stats.in_spec_pct >= 95
                ? "green"
                : spc.data.stats.in_spec_pct >= 85
                  ? "amber"
                  : "red"
            }
          />
        </div>
      )}

      {/* Process Capability */}
      {spc.data && !spc.data.unavailable && spc.data.stats.stddev > 0 && (() => {
        const { mean, stddev } = spc.data.stats;
        const { error_low, error_high } = spc.data.spec;
        const hasLSL = error_low != null;
        const hasUSL = error_high != null;
        if (!hasLSL && !hasUSL) return null;

        const cp = hasLSL && hasUSL
          ? (error_high - error_low) / (6 * stddev)
          : null;
        const cpuVal = hasUSL ? (error_high - mean) / (3 * stddev) : null;
        const cplVal = hasLSL ? (mean - error_low) / (3 * stddev) : null;
        const cpk = cpuVal != null && cplVal != null
          ? Math.min(cpuVal, cplVal)
          : cpuVal ?? cplVal ?? null;

        const ratingOf = (v: number) =>
          v >= 1.67 ? { label: "Excellent", color: "text-emerald-400" }
          : v >= 1.33 ? { label: "Capable", color: "text-emerald-400" }
          : v >= 1.0 ? { label: "Marginal", color: "text-amber-400" }
          : { label: "Incapable", color: "text-red-400" };

        return (
          <div className="rounded-xl border border-border bg-card p-5">
            <h2 className="mb-3 text-lg font-semibold text-card-foreground">
              Process Capability
              <HelpIcon k="cpk" />
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {cp != null && (
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Cp</p>
                  <p className={`text-2xl font-bold ${ratingOf(cp).color}`}>{cp.toFixed(2)}</p>
                  <p className={`text-xs ${ratingOf(cp).color}`}>{ratingOf(cp).label}</p>
                </div>
              )}
              {cpk != null && (
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Cpk</p>
                  <p className={`text-2xl font-bold ${ratingOf(cpk).color}`}>{cpk.toFixed(2)}</p>
                  <p className={`text-xs ${ratingOf(cpk).color}`}>{ratingOf(cpk).label}</p>
                </div>
              )}
              {cpuVal != null && (
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Cpu (upper)</p>
                  <p className="text-lg font-semibold text-card-foreground">{cpuVal.toFixed(2)}</p>
                </div>
              )}
              {cplVal != null && (
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Cpl (lower)</p>
                  <p className="text-lg font-semibold text-card-foreground">{cplVal.toFixed(2)}</p>
                </div>
              )}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Based on {formatNumber(spc.data.stats.count)} samples.
              {cp != null ? ` Cp = (USL−LSL) / 6σ.` : ""}
              {" "}Cpk = min(Cpu, Cpl) — measures centering within spec limits.
              {" "}Target: ≥1.33 capable, ≥1.67 excellent.
            </p>
          </div>
        );
      })()}

      {/* Weight Drift Model */}
      {strengthModel.data && strengthModel.data.model && (
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-card-foreground">
                Weight Drift Model
              </h2>
              <p className="text-xs text-muted-foreground">
                Linear regression model for part weight trends
                <HelpIcon k="r_squared" />
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-card-foreground">
                R² = {strengthModel.data.model.r_squared.toFixed(3)}
              </p>
              <p className="text-xs text-muted-foreground">
                n={formatNumber(strengthModel.data.model.sample_count)}, RMSE={strengthModel.data.model.rmse.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* In-Spec % Trending */}
      {inSpecTrend.data && inSpecTrend.data.data.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-card-foreground">
                In-Spec % Trend
                <HelpIcon k="in_spec" />
              </h2>
              <p className="text-xs text-muted-foreground">
                Percentage of samples within error limits over time
              </p>
            </div>
            <div className="flex gap-1">
              {(["week", "month"] as const).map((b) => (
                <button
                  key={b}
                  onClick={() => setTrendBucket(b)}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    trendBucket === b
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-accent"
                  }`}
                >
                  {b === "week" ? "Weekly" : "Monthly"}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={inSpecTrend.data.data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border))" />
              <XAxis
                dataKey="bucket"
                tick={{ fontSize: 11, fill: "var(--muted-foreground))" }}
                tickFormatter={(v: string) => {
                  const d = new Date(v);
                  return d.toLocaleDateString("en-AU", { month: "short", year: "2-digit" });
                }}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: "var(--muted-foreground))" }}
                tickFormatter={(v: number) => `${v}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "var(--card))",
                  border: "1px solid var(--border))",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
                labelFormatter={(v: string) => {
                  const d = new Date(v);
                  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
                }}
                formatter={(value: number, name: string) => {
                  if (name === "pct") return [`${value}%`, "In Spec"];
                  if (name === "total") return [value, "Samples"];
                  return [value, name];
                }}
              />
              <ReferenceLine y={95} stroke="var(--muted-foreground))" strokeDasharray="5 5" strokeOpacity={0.5} />
              <Area
                type="monotone"
                dataKey="pct"
                stroke="hsl(142.1 76.2% 36.3%)"
                fill="hsl(142.1 76.2% 36.3%)"
                fillOpacity={0.15}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Inspector Consistency */}
      {analystData.data && analystData.data.inspectors.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="mb-3 text-lg font-semibold text-card-foreground">
            Inspector Consistency
          </h2>
          <p className="mb-3 text-xs text-muted-foreground">
            Per-inspector mean for {testLabel}. Flagged if z-score &gt;1 from the global mean ({analystData.data.global_mean}).
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-3 py-2 text-muted-foreground font-medium">Operator</th>
                  <th className="px-3 py-2 text-right text-muted-foreground font-medium">Samples</th>
                  <th className="px-3 py-2 text-right text-muted-foreground font-medium">Mean</th>
                  <th className="px-3 py-2 text-right text-muted-foreground font-medium">Std Dev</th>
                  <th className="px-3 py-2 text-right text-muted-foreground font-medium">Z-Score</th>
                  <th className="px-3 py-2 text-muted-foreground font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {analystData.data.inspectors.map((a: { inspector: string; count: number; mean: number; stddev: number; deviation_from_global: number; flagged: boolean }) => {
                  return (
                  <tr key={a.inspector} className="border-b border-border/50 hover:bg-muted/50">
                    <td className="px-3 py-2 font-medium text-foreground">{a.inspector}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">{formatNumber(a.count)}</td>
                    <td className="px-3 py-2 text-right">{a.mean}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">{a.stddev}</td>
                    <td className={`px-3 py-2 text-right font-medium ${
                      a.flagged ? "text-red-400" : "text-foreground"
                    }`}>
                      {a.deviation_from_global > 0 ? "+" : ""}{a.deviation_from_global.toFixed(2)}σ
                    </td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        a.flagged
                          ? "bg-red-500/20 text-red-400"
                          : "bg-emerald-500/20 text-emerald-400"
                      }`}>
                        {a.flagged ? "Flagged" : "Normal"}
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {analystData.data.inspectors.some((a: { flagged: boolean }) => a.flagged) && (
            <p className="mt-3 text-xs text-amber-400">
              Flagged inspectors show mean values &gt;1σ from the global mean. This may indicate systematic measurement bias or different testing techniques.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  status,
  helpKey,
}: {
  label: string;
  value: string;
  status?: "green" | "amber" | "red";
  helpKey?: string;
}) {
  const colors = {
    green: "text-emerald-400",
    amber: "text-amber-400",
    red: "text-red-400",
  };

  return (
    <div className="rounded-lg border border-border bg-card p-3 text-center">
      <p className="text-xs text-muted-foreground">
        {label}
        {helpKey && <HelpIcon k={helpKey} align="center" />}
      </p>
      <p
        className={`text-lg font-bold ${status ? colors[status] : "text-card-foreground"}`}
      >
        {value}
      </p>
    </div>
  );
}

/** Renders all available fields from a full sample record */
function SampleDetail({ data }: { data: Record<string, unknown> }) {
  const num = (key: string) => data[key] != null ? Number(data[key]) : null;
  const fmt = (v: number | null, dp = 2) => v != null ? v.toFixed(dp) : null;

  const groups: { label: string; items: { label: string; value: string }[] }[] = [];

  // Dimensional measurements
  const dims = [
    num("weight_g") != null ? { label: "Weight", value: `${fmt(num("weight_g"))} g` } : null,
    num("dim_length_mm") != null ? { label: "Length", value: `${fmt(num("dim_length_mm"))} mm` } : null,
    num("dim_width_mm") != null ? { label: "Width", value: `${fmt(num("dim_width_mm"))} mm` } : null,
    num("dim_height_mm") != null ? { label: "Height", value: `${fmt(num("dim_height_mm"))} mm` } : null,
    num("dim_diameter_mm") != null ? { label: "Diameter", value: `${fmt(num("dim_diameter_mm"))} mm` } : null,
    num("dim_wall_thickness_mm") != null ? { label: "Wall Thickness", value: `${fmt(num("dim_wall_thickness_mm"))} mm` } : null,
  ].filter(Boolean) as { label: string; value: string }[];
  if (dims.length) groups.push({ label: "Measurements", items: dims });

  // Process parameters
  const process = [
    num("cycle_time_s") != null ? { label: "Cycle Time", value: `${fmt(num("cycle_time_s"))} s` } : null,
    num("cushion_mm") != null ? { label: "Cushion", value: `${fmt(num("cushion_mm"))} mm` } : null,
    num("pack_pressure_bar") != null ? { label: "Pack Pressure", value: `${fmt(num("pack_pressure_bar"))} bar` } : null,
    num("delta_e") != null ? { label: "Colour (ΔE)", value: `${fmt(num("delta_e"))}` } : null,
  ].filter(Boolean) as { label: string; value: string }[];
  if (process.length) groups.push({ label: "Process", items: process });

  // Metadata
  const meta: { label: string; value: string }[] = [];
  if (data.part_name) meta.push({ label: "Part", value: String(data.part_name) });
  if (data.material) meta.push({ label: "Material", value: String(data.material) });
  if (data.operator) meta.push({ label: "Operator", value: String(data.operator) });
  if (data.shift) meta.push({ label: "Shift", value: String(data.shift) });
  if (data.machine_id) meta.push({ label: "Machine", value: String(data.machine_id) });
  if (data.notes && String(data.notes).trim()) meta.push({ label: "Notes", value: String(data.notes) });
  if (meta.length) groups.push({ label: "Sample Info", items: meta });

  if (groups.length === 0) {
    return <p className="text-xs text-muted-foreground">No additional test data recorded for this sample.</p>;
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {groups.map((g) => (
        <div key={g.label}>
          <p className="mb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{g.label}</p>
          <div className="space-y-0.5">
            {g.items.map((item) => (
              <div key={item.label} className="flex justify-between text-xs">
                <span className="text-muted-foreground">{item.label}</span>
                <span className="font-medium text-foreground">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
